import React, { forwardRef } from 'react';
import { CaretDownIcon } from '../../icon/icons/caret-down';
import type { PolymorphicComponentPropWithRef, PolymorphicRef } from '../../type-utils/polymorphic';
import { AccordionBody } from './atoms/accordion-body';
import { AccordionHeader } from './atoms/accordion-header';
import { AccordionItem } from './atoms/accordion-item';
import { AccordionProviderProps, AccordionProvider } from './provider/accordion.provider';

export type AccordionProps<
  C extends React.ElementType,
  Multiple extends boolean = false
> = PolymorphicComponentPropWithRef<C, AccordionProviderProps<Multiple>>;

type AccordionComponent = <C extends React.ElementType = 'section', Multiple extends boolean = false>(
  props: AccordionProps<C, Multiple>
) => React.ReactNode;

export const AccordionOptional: AccordionComponent = forwardRef(
  <C extends React.ElementType = 'section', Multiple extends boolean = false>(
    {
      shouldLoop,
      children,
      isMulti,
      controlledValue,
      startingValue,
      onChange,
      onAnimationStart,
      onAnimationEnd,
      onAnimationComplete,
      chevronPosition = 'right',
      isInteractive = false,
      headingLevel,
      shouldRenderBody,
      chevronIcon = CaretDownIcon,
      chevronSize,
      variant = 'default',
      padding,
      distance,
      hasMultiLocations,
      size = 'small',
      showBoxShadow,
      showBorderBottomAlways,
      startAdornment,
      ...others
    }: AccordionProps<C, Multiple>,
    ref: PolymorphicRef<C>
  ) => {
    return (
      <AccordionProvider
        isMulti={isMulti}
        controlledValue={controlledValue}
        startingValue={startingValue}
        onChange={onChange}
        shouldLoop={shouldLoop}
        onAnimationStart={onAnimationStart}
        onAnimationEnd={onAnimationEnd}
        onAnimationComplete={onAnimationComplete}
        chevronPosition={chevronPosition}
        isInteractive={isInteractive}
        headingLevel={headingLevel}
        chevronIcon={chevronIcon}
        variant={variant}
        shouldRenderBody={shouldRenderBody}
        padding={padding}
        chevronSize={chevronSize}
        distance={distance}
        size={size}
        hasMultiLocations={hasMultiLocations}
        showBoxShadow={showBoxShadow}
        showBorderBottomAlways={showBorderBottomAlways}
        startAdornment={startAdornment}
      >
        <div ref={ref} {...others} data-accordion>
          {children}
        </div>
      </AccordionProvider>
    );
  }
);

const AccordionNamespace = Object.assign(AccordionOptional, {
  Item: AccordionItem,
  Header: AccordionHeader,
  Body: AccordionBody,
});

export { AccordionNamespace as Accordion };
